import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses (token expired/invalid)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface Campaign {
  id: number;
  name: string;
  subject: string;
  htmlContent: string;
  fromEmail: string;
  status: 'draft' | 'scheduled' | 'sending' | 'completed' | 'failed';
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  startedAt?: string;
  completedAt?: string;
  archived: boolean;
  designJson?: string;
  channelId?: number;
  textContent?: string;
  viberRichContent?: string;
  fallbackEnabled?: boolean;
  fallbackSmsContent?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignStats {
  total: number;
  sent: number;
  delivered?: number;
  undelivered?: number;
  failed: number;
  pending: number;
  sending: number;
  cost?: { rsd: number; eur: number };
  successRate: string;
  status: string;
}

export interface Contact {
  id: number;
  email?: string | null;
  name?: string;
  phoneNumber?: string | null;
  customFields?: Record<string, any>;
  unsubscribed: boolean;
  unsubscribedAt?: string;
  blacklisted?: boolean;
  lists?: ContactList[];
  createdAt: string;
  updatedAt: string;
}

export interface ContactList {
  id: number;
  name: string;
  description?: string;
  contactCount?: number;
  subscribedCount?: number;
  unsubscribedCount?: number;
  contacts?: Contact[];
  createdAt: string;
  updatedAt: string;
}

// Campaign APIs
export const campaignAPI = {
  getAll: (showArchived = false) =>
    api.get<{ success: boolean; data: Campaign[] }>('/campaigns', { params: { showArchived } }),

  getOne: (id: number) => api.get<{ success: boolean; data: { campaign: Campaign; stats: CampaignStats } }>(`/campaigns/${id}`),

  create: (data: { name: string; subject: string; htmlContent: string; fromEmail: string; designJson?: string }) =>
    api.post<{ success: boolean; data: Campaign }>('/campaigns', data),

  update: (id: number, data: Partial<Campaign>) =>
    api.put<{ success: boolean; data: Campaign }>(`/campaigns/${id}`, data),

  delete: (id: number) => api.delete(`/campaigns/${id}`),

  archive: (id: number) =>
    api.post<{ success: boolean; data: Campaign; message: string }>(`/campaigns/${id}/archive`),

  clone: (id: number) =>
    api.post<{ success: boolean; data: { campaign: Campaign }; message: string }>(`/campaigns/${id}/clone`),

  start: (id: number) => api.post(`/campaigns/${id}/start`),

  preview: (id: number) =>
    api.get<{ success: boolean; data: { subject: string; htmlContent: string; sampleData: any } }>(`/campaigns/${id}/preview`),

  getStats: (id: number) =>
    api.get<{ success: boolean; data: CampaignStats }>(`/campaigns/${id}/stats`),

  // Campaign Lists
  attachLists: (id: number, listIds: number[]) =>
    api.post(`/campaigns/${id}/lists`, { listIds }),

  getLists: (id: number) =>
    api.get<{ success: boolean; data: { lists: ContactList[] } }>(`/campaigns/${id}/lists`),

  removeList: (id: number, listId: number) =>
    api.delete(`/campaigns/${id}/lists/${listId}`),

  generateRecipients: (id: number) =>
    api.post(`/campaigns/${id}/generate-recipients`),

  // Viber-specific endpoints
  createViberCampaign: (data: {
    name: string;
    channelId: number;
    viberContent: any;
    fallbackEnabled?: boolean;
    fallbackSmsContent?: string;
  }) =>
    api.post<{ success: boolean; data: { campaign: Campaign; messageType: string } }>('/campaigns/viber', data),

  updateViberContent: (id: number, data: {
    viberContent: any;
    fallbackEnabled?: boolean;
    fallbackSmsContent?: string;
  }) =>
    api.put<{ success: boolean; data: Campaign }>(`/campaigns/${id}/viber-content`, data),

  previewViberMessage: (id: number, recipientData?: any) =>
    api.post<{
      success: boolean;
      data: {
        original: any;
        personalized: any;
        recipient: any;
        messageType: string;
        warnings: string[];
      };
    }>(`/campaigns/${id}/preview-viber`, { recipientData }),

  getViberStats: (id: number) =>
    api.get<{
      success: boolean;
      data: {
        total: number;
        sent: number;
        dispatched?: number;
        delivered: number;
        undelivered?: number;
        seen: number;
        failed: number;
        pending: number;
        cost?: { rsd: number; eur: number };
        totalCost: number;
        recentSends: number;
        deliveryRate: string;
        failureRate: string;
      };
    }>(`/campaigns/${id}/viber-stats`),

  // Export campaign recipients filtered by delivery status (delivered|seen|undelivered)
  exportRecipients: (id: number, status: string) =>
    api.get(`/campaigns/${id}/recipients/export`, {
      params: { status },
      responseType: 'blob',
    }),

  // Test sends (single ad-hoc recipient)
  sendTestViber: (id: number, phoneNumber: string) =>
    api.post<{ success: boolean; message: string; data: { messageId: string; status: string } }>(
      `/campaigns/${id}/test-viber`,
      { phoneNumber }
    ),

  sendTestEmail: (id: number, email: string) =>
    api.post<{ success: boolean; message: string; data: { messageId: string } }>(
      `/campaigns/${id}/test-email`,
      { email }
    ),

  // Ad-hoc test sends from form content (no saved campaign required)
  sendTestViberAdhoc: (data: { phoneNumber: string; viberContent: any; fallbackEnabled?: boolean; fallbackSmsContent?: string }) =>
    api.post<{ success: boolean; message: string; data: { messageId: string; status: string } }>('/campaigns/test/viber', data),

  sendTestEmailAdhoc: (data: { email: string; subject: string; htmlContent: string }) =>
    api.post<{ success: boolean; message: string; data: { messageId: string } }>('/campaigns/test/email', data),

  // Scheduling
  getSchedule: (id: number) =>
    api.get<{ success: boolean; data: { schedule: any | null } }>(`/campaigns/${id}/schedule`),

  scheduleCampaign: (id: number, data: { scheduledAt: string; scheduleType?: string; recurrenceEndAt?: string }) =>
    api.post<{ success: boolean; message: string; data: { schedule: any } }>(`/campaigns/${id}/schedule`, data),

  cancelSchedule: (id: number) =>
    api.delete<{ success: boolean; message: string }>(`/campaigns/${id}/schedule`),
};

// Tenant (Super Admin) types & APIs
export interface TenantConfigPublic {
  id?: number;
  awsRegion?: string | null;
  awsSesFromEmail?: string | null;
  awsSesFromName?: string | null;
  sesRateLimit?: number | null;
  nthUsername?: string | null;
  nthApiUrl?: string | null;
  nthRateLimit?: number | null;
  nthFromName?: string | null;
}

export interface TenantReportRow {
  id: number;
  name: string;
  channel: 'email' | 'sms' | 'viber';
  status: string;
  createdAt: string;
  recipients: number;
  emailCost: number;
  smsCost: number;
  viberCost: number;
  smsIsPhantom: boolean;
  smsPhantomPct: number;
  smsPhantomRecipients: number;
  total: number;
}

export interface TenantReport {
  tenant: { id: number; name: string; slug: string };
  period: { from: string; to: string };
  prices: { email: number; sms: number; viber: number; currency: string };
  failoverEnabled: boolean;
  campaigns: TenantReportRow[];
  totals: {
    email: number;
    sms: number;
    viber: number;
    subtotal: number;
    serviceFee: number;
    grandTotal: number;
    currency?: string;
    eurRate?: number;
    subtotalEur?: number;
    grandTotalEur?: number;
  };
}

export interface Tenant {
  id: number;
  name: string;
  slug: string;
  domain?: string | null;
  isActive: boolean;
  subscriptionTier: 'basic' | 'pro' | 'enterprise';
  subscriptionExpiresAt?: string | null;
  maxContacts: number;
  maxCampaignsPerMonth: number;
  maxEmailsPerMonth: number;
  maxSmsPerMonth: number;
  maxUsers: number;
  emailEnabled?: boolean;
  smsEnabled?: boolean;
  viberEnabled?: boolean;
  smsFallbackEnabled?: boolean;
  messagesOverviewEnabled?: boolean;
  priceEmail?: number;
  priceSms?: number;
  priceViber?: number;
  serviceFee?: number;
  timezone?: string;
  language?: string;
  config?: TenantConfigPublic;
  createdAt: string;
  updatedAt?: string;
}

export const tenantAPI = {
  getAll: () =>
    api.get<{ success: boolean; data: { tenants: Tenant[]; count: number } }>('/tenants'),

  getOne: (id: number) =>
    api.get<{ success: boolean; data: { tenant: Tenant } }>(`/tenants/${id}`),

  create: (data: { name: string; slug: string; domain?: string; subscriptionTier?: 'basic' | 'pro' | 'enterprise' }) =>
    api.post<{ success: boolean; data: { tenant: Tenant }; message: string }>('/tenants', data),

  update: (id: number, data: Partial<Tenant>) =>
    api.put<{ success: boolean; data: { tenant: Tenant }; message: string }>(`/tenants/${id}`, data),

  // Backend requires explicit confirmation in the body.
  remove: (id: number) =>
    api.delete<{ success: boolean; message: string }>(`/tenants/${id}`, { data: { confirm: 'DELETE' } }),

  getStats: (id: number) =>
    api.get<{ success: boolean; data: any }>(`/tenants/${id}/stats`),

  // Per-tenant spend report for a period (super admin only).
  getReport: (id: number, params: { from?: string; to?: string }) =>
    api.get<{ success: boolean; data: TenantReport }>(`/tenants/${id}/report`, { params }),

  // Email sender (from-address) for the current user's tenant (no secrets).
  getMySender: () =>
    api.get<{ success: boolean; data: { fromEmail: string | null; fromName: string | null; configured: boolean; channels: { email: boolean; sms: boolean; viber: boolean }; messagesOverview: boolean } }>(
      '/tenants/me/sender'
    ),

  // Update per-tenant SES / NTH credentials (stored encrypted server-side).
  updateConfig: (
    id: number,
    data: {
      awsRegion?: string;
      awsAccessKeyId?: string;
      awsSecretAccessKey?: string;
      awsSesFromEmail?: string;
      awsSesFromName?: string;
      sesRateLimit?: number;
      nthUsername?: string;
      nthApiKey?: string;
      nthApiUrl?: string;
      nthRateLimit?: number;
      nthFromName?: string;
    }
  ) =>
    api.put<{ success: boolean; data: { config: TenantConfigPublic }; message: string }>(
      `/tenants/${id}/config`,
      data
    ),
};

// User (Super Admin / Tenant Admin) types & APIs
export interface AppUser {
  id: number;
  tenantId: number | null;
  username: string;
  email: string;
  role: 'super_admin' | 'tenant_admin' | 'user';
  isActive: boolean;
  lastLoginAt?: string | null;
  loginCount: number;
  tenant?: { id: number; name: string; slug: string } | null;
  createdAt: string;
}

export const userAPI = {
  getAll: (tenantId?: number) =>
    api.get<{ success: boolean; data: { users: AppUser[]; count: number } }>('/users', {
      params: tenantId ? { tenantId } : undefined,
    }),

  create: (data: {
    username: string;
    email: string;
    password: string;
    role: 'super_admin' | 'tenant_admin' | 'user';
    tenantId?: number | null;
    isActive?: boolean;
  }) => api.post<{ success: boolean; data: { user: AppUser }; message: string }>('/users', data),

  update: (id: number, data: Partial<{ email: string; role: string; isActive: boolean; tenantId: number | null }>) =>
    api.put<{ success: boolean; data: { user: AppUser }; message: string }>(`/users/${id}`, data),

  resetPassword: (id: number, password: string) =>
    api.put<{ success: boolean; message: string }>(`/users/${id}/password`, { password }),

  remove: (id: number) => api.delete<{ success: boolean; message: string }>(`/users/${id}`),
};

// Media (upload manager) types & APIs
export interface MediaFile {
  filename: string;
  url: string;
  size: number;
  type: 'image' | 'video' | 'other';
  uploadedAt?: string;
}

export const mediaAPI = {
  upload: (file: File, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ success: boolean; data: MediaFile; message: string }>('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },

  list: () => api.get<{ success: boolean; data: { files: MediaFile[] } }>('/media'),

  remove: (filename: string) => api.delete<{ success: boolean; message: string }>(`/media/${filename}`),
};

// Contact APIs
export const contactAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string; unsubscribed?: boolean }) =>
    api.get<{ success: boolean; data: { contacts: Contact[]; total: number; page: number; pages: number } }>('/contacts', { params }),

  getOne: (id: number) =>
    api.get<{ success: boolean; data: { contact: Contact } }>(`/contacts/${id}`),

  create: (data: { email: string; name?: string; phoneNumber?: string; customFields?: Record<string, any> }) =>
    api.post<{ success: boolean; data: { contact: Contact } }>('/contacts', data),

  update: (id: number, data: Partial<Contact>) =>
    api.put<{ success: boolean; data: { contact: Contact } }>(`/contacts/${id}`, data),

  delete: (id: number) =>
    api.delete(`/contacts/${id}`),

  getProtected: (params?: { page?: number; limit?: number }) =>
    api.get<{ success: boolean; data: { contacts: Contact[]; total: number; page: number; pages: number } }>('/contacts/protected', { params }),

  getByEmail: (email: string) =>
    api.get<{ success: boolean; data: { contact: Contact } }>(`/contacts/email/${email}`),
};

// Contact List APIs
export const contactListAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get<{ success: boolean; data: { lists: ContactList[]; total: number; page: number; pages: number } }>('/contact-lists', { params }),

  getOne: (id: number) =>
    api.get<{ success: boolean; data: { list: ContactList } }>(`/contact-lists/${id}`),

  create: (data: { name: string; description?: string }) =>
    api.post<{ success: boolean; data: { list: ContactList } }>('/contact-lists', data),

  update: (id: number, data: { name?: string; description?: string }) =>
    api.put<{ success: boolean; data: { list: ContactList } }>(`/contact-lists/${id}`, data),

  delete: (id: number) =>
    api.delete(`/contact-lists/${id}`),

  getContacts: (id: number, params?: { page?: number; limit?: number }) =>
    api.get<{ success: boolean; data: { contacts: Contact[]; total: number; page: number; pages: number } }>(`/contact-lists/${id}/contacts`, { params }),

  addContact: (id: number, contactId: number) =>
    api.post(`/contact-lists/${id}/contacts`, { contactId }),

  removeContact: (id: number, contactId: number) =>
    api.delete(`/contact-lists/${id}/contacts/${contactId}`),

  uploadContacts: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ success: boolean; data: { totalUploaded: number; skipped: number; errors: number; totalProcessed: number } }>(
      `/contact-lists/${id}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },

  exportContacts: (id: number, format: 'xlsx' | 'csv' = 'xlsx') => {
    return api.get(`/contact-lists/${id}/export`, {
      params: { format },
      responseType: 'blob',
    });
  },
};

// Upload APIs
export const uploadAPI = {
  uploadRecipients: (campaignId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/upload/campaigns/${campaignId}/recipients`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  downloadTemplate: () => {
    return api.get('/upload/template', {
      responseType: 'blob',
    });
  },

  getRecipients: (campaignId: number, page: number = 1, limit: number = 50, status: string = 'all') => {
    return api.get(`/upload/campaigns/${campaignId}/recipients`, {
      params: { page, limit, status },
    });
  },
};

// ---- Sent-messages overview ("Pregled poruka") ----
export type MessageChannel = 'email' | 'sms' | 'viber';

export interface SentMessageRow {
  id: number;
  status: string;
  channel: MessageChannel;
  sentAt: string | null;
  deliveredAt: string | null;
  seenAt: string | null;
  failedAt: string | null;
  cost: number | null;
  messageId: string | null;
  error: string | null;
  campaign: { id: number; name: string; isTransactional: boolean } | null;
  recipient: { name: string | null; email: string | null; phone: string | null } | null;
}

export interface MessageOverviewSummary {
  total: number;
  delivered: number;
  failed: number;
  undelivered: number;
  cost: number;
}

export interface MessageDetail extends Omit<SentMessageRow, 'campaign'> {
  campaign: { id: number; name: string; isTransactional: boolean } | null;
  content: {
    channel: MessageChannel;
    subject?: string;
    html?: string;
    text?: string;
    viber?: { text: string; image: string | null; button: { caption: string; action: string } | null };
    fallbackSms?: string;
  };
}

export interface MessageOverviewParams {
  page?: number;
  limit?: number;
  status?: string;
  channel?: string;
  search?: string;
  from?: string;
  to?: string;
  tenant_id?: number;
  contactId?: number;
}

export const messageAPI = {
  getOverview: (params?: MessageOverviewParams) =>
    api.get<{
      success: boolean;
      data: {
        messages: SentMessageRow[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
        summary: MessageOverviewSummary;
      };
    }>('/messages/overview', { params }),

  getDetail: (id: number, tenantId?: number) =>
    api.get<{ success: boolean; data: MessageDetail }>(`/messages/overview/${id}`, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    }),
};

export default api;
